import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { inr } from "@/lib/api";

const FREE_SHIP = 800;

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, subtotal } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col bg-[#fdfbf7]" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-rose-950">Your Shopping Bag</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <ShoppingBag className="w-16 h-16 text-rose-950/20" />
            <p className="text-muted-foreground">Your bag is empty</p>
            <Button onClick={() => { setOpen(false); navigate("/shop"); }} className="bg-rose-950 hover:bg-rose-900" data-testid="cart-empty-shop-btn">Start Shopping</Button>
          </div>
        ) : (
          <>
            <div className="py-3">
              <div className="flex justify-between text-xs font-accent mb-1">
                <span>{remaining > 0 ? `Add ${inr(remaining)} for FREE shipping` : "🎉 You unlocked FREE shipping!"}</span>
              </div>
              <div className="h-1.5 bg-rose-900/10 rounded-full overflow-hidden">
                <div className="h-full bg-amber-600 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3 border-b border-rose-900/10 pb-4" data-testid={`cart-item-${i.sku}`}>
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover rounded-md" />
                  <div className="flex-1">
                    <p className="font-medium text-sm leading-tight">{i.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{i.size} · {i.color}</p>
                    <p className="text-sm font-semibold text-rose-950 mt-1">{inr(i.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQty(i.key, i.qty - 1)} className="w-6 h-6 border rounded flex items-center justify-center" data-testid={`cart-dec-${i.sku}`}><Minus className="w-3 h-3" /></button>
                      <span className="text-sm w-6 text-center">{i.qty}</span>
                      <button onClick={() => updateQty(i.key, i.qty + 1)} className="w-6 h-6 border rounded flex items-center justify-center" data-testid={`cart-inc-${i.sku}`}><Plus className="w-3 h-3" /></button>
                      <button onClick={() => remove(i.key)} className="ml-auto text-rose-700" data-testid={`cart-remove-${i.sku}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-rose-900/10 pt-4 space-y-3">
              <div className="flex justify-between font-serif text-lg">
                <span>Subtotal</span>
                <span className="font-semibold" data-testid="cart-subtotal">{inr(subtotal)}</span>
              </div>
              <Button className="w-full bg-rose-950 hover:bg-rose-900 h-12 text-base" onClick={() => { setOpen(false); navigate("/checkout"); }} data-testid="cart-checkout-btn">
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
